import { useMemo, useState, type FormEvent } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
  DONE_WINDOW_DAYS,
  QUADRANT_EMPTY_COPY_PT,
  QUADRANT_LABEL_PT,
  type Quadrant as QuadrantId,
  type Task,
} from '@cleartask/shared';
import { formatDueLabel, isOverdue } from '../../lib/datetime.js';
import { groupByDueHorizon } from '../../lib/grouping.js';
import { useTimeZone } from '../../session.js';
import { useLists } from '../lists/useLists.js';
import { QUADRANT_ORDER } from './useQuadrant.js';

export const QUADRANT_DROPPABLE_PREFIX = 'quadrant-';

const ROMANOS: Record<QuadrantId, string> = { 1: 'I', 2: 'II', 3: 'III', 4: 'IV' };
const COR: Record<QuadrantId, string> = {
  1: 'var(--q1)',
  2: 'var(--q2)',
  3: 'var(--q3)',
  4: 'var(--q4)',
};

interface QuadrantProps {
  quadrant: QuadrantId;
  tasks: Task[];
  /** Concluidas deste quadrante, para o painel recolhivel do rodape. */
  done: Task[];
  now: number;
  onMove: (task: Task, quadrant: QuadrantId) => void;
  onReset: (task: Task) => void;
  onComplete: (task: Task) => void;
  onReopen: (task: Task) => void;
  onOpen: (task: Task) => void;
  /** Cria ja FIXADA neste quadrante. Ver `QuadrantAdd`. */
  onAdd: (title: string, quadrant: QuadrantId) => void;
}

/**
 * Um quadrante da matriz: alvo de drop e coluna rolavel.
 *
 * A rolagem e INTERNA a cada quadrante (spec 9.2). Com a pagina inteira
 * rolando, um quadrante cheio empurraria os outros tres para fora da tela — e
 * a matriz existe justamente para mostrar os quatro ao mesmo tempo.
 */
export function Quadrant({
  quadrant,
  tasks,
  done,
  now,
  onMove,
  onReset,
  onComplete,
  onReopen,
  onOpen,
  onAdd,
}: QuadrantProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `${QUADRANT_DROPPABLE_PREFIX}${quadrant}` });
  const timeZone = useTimeZone();

  // Um quadrante cheio era uma lista plana: o card que venceu semana passada
  // ficava visualmente igual ao que vence em vinte dias, e a barra de urgencia
  // sozinha nao da a leitura de "quanto tem em atraso aqui".
  const grupos = useMemo(
    () => groupByDueHorizon(tasks, now, timeZone),
    [tasks, now, timeZone],
  );

  const nomeDaLista = useNomeDaLista();
  const [recolhidos, setRecolhidos] = useState<ReadonlySet<string>>(new Set());

  function alternar(horizon: string) {
    setRecolhidos((atual) => {
      const novo = new Set(atual);
      if (novo.has(horizon)) novo.delete(horizon);
      else novo.add(horizon);
      return novo;
    });
  }

  return (
    // Desenho do TickTick (§8ab): cartao branco arredondado sobre o cinza da
    // pagina, sem a faixa colorida no topo; a cor fica no numeral e no titulo.
    <section
      ref={setNodeRef}
      aria-label={`${ROMANOS[quadrant]} — ${QUADRANT_LABEL_PT[quadrant]}`}
      className={[
        'flex min-h-0 min-w-0 flex-col rounded-lg bg-surface transition-shadow duration-150',
        isOver ? 'ring-2 ring-brand-blue' : '',
      ].join(' ')}
    >
      <header className="flex items-center gap-1.5 px-2.5 pb-1 pt-2.5">
        <span
          aria-hidden="true"
          className="grid h-[18px] min-w-[18px] shrink-0 place-items-center rounded-full px-1 font-display text-[10px] font-bold leading-none text-white"
          style={{ backgroundColor: COR[quadrant] }}
        >
          {ROMANOS[quadrant]}
        </span>
        <h2
          className="min-w-0 flex-1 truncate text-sm font-medium"
          style={{ color: COR[quadrant] }}
        >
          {QUADRANT_LABEL_PT[quadrant]}
        </h2>
        {/* Contagem discreta: o TickTick nao a mostra, mas "quanto tem aqui" e
            a primeira leitura de uma triagem — e so o pendente (§8g). */}
        <span className="text-xs tabular-nums text-text-mut">{tasks.length}</span>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2.5 pb-2">
        {tasks.length === 0 ? (
          <EstadoVazio quadrant={quadrant} />
        ) : (
          <div className="flex flex-col">
            {grupos.map((grupo) => {
              const recolhido = recolhidos.has(grupo.horizon);
              return (
                <section key={grupo.horizon} aria-label={grupo.label}>
                  {/*
                    Subgrupo recolhivel ("Em atraso 2"), como no TickTick web.
                    So a partir do `md`: no celular o TickTick mostra a lista
                    corrida, e cada cabecalho custaria uma linha de um quadrante
                    que ja tem um quarto da tela. Com uma faixa so, nao aparece
                    em lugar nenhum — "Hoje" acima da unica pilha nao distingue
                    nada de nada.
                  */}
                  {grupos.length > 1 ? (
                    <h3 className="hidden md:block">
                      <button
                        type="button"
                        onClick={() => alternar(grupo.horizon)}
                        aria-expanded={!recolhido}
                        className="flex w-full items-baseline gap-1.5 py-1 text-left text-xs font-semibold"
                      >
                        <span
                          aria-hidden="true"
                          className={[
                            'inline-block text-[9px] text-text-mut transition-transform duration-150',
                            recolhido ? '-rotate-90' : '',
                          ].join(' ')}
                        >
                          ▼
                        </span>
                        <span>{grupo.label}</span>
                        <span className="font-normal tabular-nums text-text-mut">
                          {grupo.tasks.length}
                        </span>
                      </button>
                    </h3>
                  ) : null}

                  <ul className={recolhido ? 'md:hidden' : undefined}>
                    {grupo.tasks.map((task) => (
                      <MatrixCard
                        key={task.id}
                        task={task}
                        now={now}
                        lista={task.listId === null ? null : (nomeDaLista.get(task.listId) ?? null)}
                        onMove={onMove}
                        onReset={onReset}
                        onComplete={onComplete}
                        onOpen={onOpen}
                      />
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}

        <QuadrantAdd quadrant={quadrant} onAdd={onAdd} />

        <Concluidas
          quadrant={quadrant}
          tasks={done}
          onReopen={onReopen}
          onOpen={onOpen}
        />
      </div>
    </section>
  );
}

/** Nome de cada lista, por id — a coluna cinza a direita do TickTick web. */
function useNomeDaLista(): Map<string, string> {
  const { data } = useLists();
  return useMemo(() => new Map((data?.lists ?? []).map((l) => [l.id, l.name])), [data]);
}

/**
 * Painel recolhivel com o que ja foi feito neste quadrante.
 *
 * **Fechado por padrao**, ao contrario do grupo "executados" da aba Tarefas.
 * A diferenca e o que cada tela responde: a aba Tarefas e onde se olha o que
 * foi feito, e a matriz e onde se decide o que fazer AGORA. Quatro quadrantes
 * competindo pela mesma viewport nao podem gastar altura com trabalho
 * encerrado antes de alguem pedir.
 *
 * O contador fica no botao mesmo fechado: "3 concluídas" ja responde a
 * pergunta sem custar espaco, e sem ele o painel seria uma gaveta que ninguem
 * sabe se vale abrir.
 *
 * Os cards aqui NAO sao arrastaveis e nao tem seletor de quadrante. Reclassificar
 * o que ja foi feito nao muda decisao nenhuma, e os dois controles existem para
 * decidir o que vem antes.
 */
function Concluidas({
  quadrant,
  tasks,
  onReopen,
  onOpen,
}: {
  quadrant: QuadrantId;
  tasks: Task[];
  onReopen: (task: Task) => void;
  onOpen: (task: Task) => void;
}) {
  const [aberto, setAberto] = useState(false);

  // Sem nada concluido o painel nao existe: um "0 concluídas" permanente seria
  // uma linha morta em cada um dos quatro quadrantes.
  if (tasks.length === 0) return null;

  return (
    <section aria-label={`Concluídas em ${QUADRANT_LABEL_PT[quadrant]}`} className="mt-1">
      <button
        type="button"
        onClick={() => setAberto((valor) => !valor)}
        aria-expanded={aberto}
        className="min-h-8 w-full rounded-sm px-1 text-left text-xs text-text-mut hover:bg-surface-2"
      >
        <span aria-hidden="true" className="mr-1 inline-block">
          {aberto ? '▾' : '▸'}
        </span>
        <span className="tabular-nums">{tasks.length}</span> concluída
        {tasks.length > 1 ? 's' : ''}
        {/*
          O recorte fica escrito no proprio botao. A gaveta trazia tudo que ja
          foi concluido naquele quadrante, para sempre; agora ela e uma janela,
          e uma janela que nao se anuncia parece uma lista com buracos.
        */}
        <span> · {DONE_WINDOW_DAYS} dias</span>
      </button>

      {aberto ? (
        <ul className="mt-1 flex flex-col gap-1">
          {tasks.map((task) => (
            <li
              key={task.id}
              data-task-id={task.id}
              className="flex items-center gap-2 rounded-sm border border-border bg-surface px-2 py-1"
            >
              <input
                type="checkbox"
                checked
                onChange={() => onReopen(task)}
                aria-label={`Reabrir ${task.title}`}
                className="h-4 w-4 shrink-0 accent-brand-teal"
              />

              <button
                type="button"
                onClick={() => onOpen(task)}
                className="min-w-0 flex-1 truncate py-1 text-left text-sm text-text-mut line-through"
              >
                {task.title}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * Entrada rapida do quadrante.
 *
 * Fica DENTRO da area rolavel, depois dos cards, e nao no cabecalho: a matriz
 * e uma tela de triagem, e o cabecalho carrega a contagem, que e o que se le de
 * relance. Um campo permanente ali roubaria altura dos quatro quadrantes ao
 * mesmo tempo.
 *
 * A tarefa nasce FIXADA no quadrante (`quadrantAuto: false`) — a mesma escrita
 * que arrastar um card produz. Deixar na derivacao automatica faria toda tarefa
 * criada sem prazo e sem prioridade cair em Q4, inclusive a que a pessoa acabou
 * de digitar dentro do Q1: adicionar num lugar e ver aparecer em outro.
 *
 * O preco esta dito na tela ("fixada aqui"), e o seletor do card devolve ao
 * automatico quando a pessoa quiser.
 */
function QuadrantAdd({
  quadrant,
  onAdd,
}: {
  quadrant: QuadrantId;
  onAdd: (title: string, quadrant: QuadrantId) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [titulo, setTitulo] = useState('');

  function enviar(event: FormEvent) {
    event.preventDefault();

    const limpo = titulo.trim();
    if (limpo === '') return;

    onAdd(limpo, quadrant);
    // O campo continua aberto e vazio: triagem costuma vir em rajada, e fechar
    // depois de cada item obrigaria a clicar no "+" outra vez.
    setTitulo('');
  }

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="mt-0.5 min-h-8 w-full rounded-sm px-1 text-left text-xs text-text-mut hover:bg-surface-2 hover:text-brand-blue"
      >
        + Nova tarefa aqui
      </button>
    );
  }

  return (
    <form onSubmit={enviar} className="mt-1">
      <label className="sr-only" htmlFor={`add-${quadrant}`}>
        Nova tarefa fixada em {QUADRANT_LABEL_PT[quadrant]}
      </label>
      <input
        id={`add-${quadrant}`}
        autoFocus
        value={titulo}
        onChange={(event) => setTitulo(event.target.value)}
        onBlur={() => {
          if (titulo.trim() === '') setAberto(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setTitulo('');
            setAberto(false);
          }
        }}
        placeholder="Título — fixada aqui"
        className="min-h-touch w-full rounded-sm border border-border bg-surface px-2 text-sm"
      />
    </form>
  );
}

/**
 * Estado vazio instrutivo, nao decorativo (spec 9.2): Q3 vazio diz "delegue
 * algo aqui", Q1 vazio diz que nada esta pegando fogo. As frases vivem em
 * `QUADRANT_EMPTY_COPY_PT`, junto do resto do dominio.
 */
function EstadoVazio({ quadrant }: { quadrant: QuadrantId }) {
  // "Sem tarefas" no centro do cartao, como no TickTick; a frase da spec 9.2
  // vem logo abaixo, menor — o vazio continua dizendo o que fazer com ele.
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-1 px-2 py-4 text-center">
      <p className="text-sm text-text-mut">Sem tarefas</p>
      <p className="text-xs text-text-mut opacity-80">{QUADRANT_EMPTY_COPY_PT[quadrant]}</p>
    </div>
  );
}

interface MatrixCardProps {
  task: Task;
  now: number;
  /** Nome da lista, para a coluna cinza do desktop. */
  lista: string | null;
  onMove: (task: Task, quadrant: QuadrantId) => void;
  onReset: (task: Task) => void;
  onComplete: (task: Task) => void;
  onOpen: (task: Task) => void;
}

/**
 * Uma linha de tarefa, no desenho do TickTick (§8ab): caixinha com a borda na
 * cor do quadrante, titulo, e um fio fino separando da proxima. Nada de cartao
 * com borda e faixa.
 *
 * - **Celular:** a data vai embaixo do titulo, em vermelho se venceu e em azul
 *   se nao — o titulo pode quebrar linha, como no app.
 * - **Desktop:** tudo numa linha; a lista em cinza e a data colorida a direita.
 *
 * A LINHA INTEIRA arrasta (sem a alca de antes). Os sensores da matriz separam
 * o gesto: 8 px com mouse, toque longo no celular (`MatrixBoard.tsx`).
 */
function MatrixCard({ task, now, lista, onMove, onReset, onComplete, onOpen }: MatrixCardProps) {
  const timeZone = useTimeZone();
  const { listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });

  const vencida = isOverdue(task.dueAt, now);
  const cor = COR[task.quadrant ?? 4];

  return (
    <li
      ref={setNodeRef}
      {...listeners}
      data-task-id={task.id}
      className={[
        // `relative` segura o <select> `sr-only` (position: absolute) dentro do
        // quadrante. Sem ancestral posicionado ele escapava do recorte da
        // rolagem e esticava a PAGINA — a matriz rolava 133 px a mais no
        // celular, embora cada quadrante ja rolasse por dentro.
        'relative flex touch-manipulation items-start gap-2 bg-surface',
        isDragging ? 'opacity-40' : '',
      ].join(' ')}
      style={
        transform
          ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 10 }
          : undefined
      }
    >
      {/*
        Concluir sem sair da matriz. `view=matrix` filtra `status = 'open'`,
        entao a linha sai da grade — o toast segura a volta por 8 s. A borda da
        caixinha leva a cor do quadrante, como no TickTick.
      */}
      <input
        type="checkbox"
        checked={false}
        onChange={() => onComplete(task)}
        onPointerDown={(event) => event.stopPropagation()}
        aria-label={`Concluir ${task.title}`}
        className="mt-[9px] h-[15px] w-[15px] shrink-0 cursor-pointer appearance-none rounded-[3px] border-[1.5px] bg-surface"
        style={{ borderColor: cor }}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-0.5 border-b border-border py-2 md:flex-row md:items-center md:gap-2">
        {/*
          O titulo abre o painel de detalhe, como na aba Tarefas e no
          calendario. Botao proprio, e nao a linha inteira: a linha e quem
          arrasta, e o clique precisa de um alvo que nao seja o gesto.
        */}
        <button
          type="button"
          onClick={() => onOpen(task)}
          className="min-w-0 text-left text-sm leading-snug md:flex-1 md:truncate"
        >
          {task.title}
        </button>

        <div className="flex shrink-0 items-center gap-1.5 text-xs">
          {lista !== null ? (
            <span className="hidden max-w-24 truncate text-text-mut md:inline">{lista}</span>
          ) : null}

          {/* Pino: marcador discreto de classificacao manual (spec 4.1). */}
          {!task.quadrantAuto ? (
            <span title="Quadrante fixado manualmente" className="text-[10px]">
              📌
            </span>
          ) : null}

          {task.dueAt !== null ? (
            <span className={['tabular-nums', vencida ? 'text-overdue' : 'text-brand-blue'].join(' ')}>
              {formatDueLabel(task.dueAt, task.allDay, now, timeZone)}
            </span>
          ) : null}

          {/*
            Caminho acessivel equivalente ao arrastar (spec 10.5). Invisivel
            ate receber foco: o TickTick nao tem o seletor, e na tela ele so
            existe para quem chega por teclado ou leitor de tela — que e
            exatamente quem nao consegue arrastar.
          */}
          <label className="sr-only" htmlFor={`move-${task.id}`}>
            Quadrante de {task.title}
          </label>
          <select
            id={`move-${task.id}`}
            value={task.quadrantAuto ? 'auto' : String(task.quadrant ?? 4)}
            onChange={(event) => {
              const valor = event.target.value;
              if (valor === 'auto') onReset(task);
              else onMove(task, Number(valor) as QuadrantId);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            className="sr-only rounded-sm border border-border bg-surface text-xs focus:not-sr-only"
          >
            <option value="auto">auto</option>
            {QUADRANT_ORDER.map((q) => (
              <option key={q} value={q}>
                {ROMANOS[q]}
              </option>
            ))}
          </select>
        </div>
      </div>
    </li>
  );
}
